/**
 * The call list.
 *
 * Every column exists to answer one question before the phone is picked up: how
 * urgent, who is it, what is wrong with their site, how do I reach them, and
 * when did the robot last confirm any of this is still true. The last one
 * matters more than it looks - a lead the autopilot verified this morning is a
 * different conversation from one it looked at in March.
 *
 * Nine table columns became seven pill tracks: severity carries the score, and
 * the platform's version moved under its name.
 *
 * The row DOES have a stretched overlay, and for a long time it deliberately
 * did not: a lead had no view of its own, and the only thing behind it was
 * somebody else's website, which should not open because a click landed near
 * it. It has one now - the scan report the lead was made from, in a dialog over
 * this page - so the row behaves like every other row in the CRM. The links
 * that were the reason for the old rule (the register, the site itself) survive
 * as small controls lifted above the overlay; see `LeadRowCells`.
 */
import { useState, type ReactNode } from "react";
import { ChevronLeft, ChevronRight, Mail, Target } from "lucide-react";
import { cn } from "@wa/ui";
import { formatWhen } from "../../../lib/siteScan";
import type { LeadListParams, LeadStatus, ScanLead } from "../../../lib/scanLeads";
import type { CallTarget } from "../../telephony/callState";
import { PhoneLink } from "../../telephony/PhoneLink";
import { QueuePick, QueuePickAll, type QueuePicks } from "../../telephony/queue";
import { ContextMenu, useContextMenu } from "../../shell/ContextMenu";
import { Btn, EmptyState, SortHead, StatusPill, fmtDateTime, type StatusTone, listCard } from "../../ui";
import type { T } from "../site-scanner/types";
import { Company, Reasons } from "./LeadRowCells";
import { LeadRowItems } from "./LeadRowItems";
import { LeadRowMenu } from "./LeadRowMenu";
import { BrainBadgeCell } from "../ariregister/brain";

/** Severity+score, company, platform, reasons, contact, checked, status, brain. */
const COLS = "1.05fr minmax(0,1.9fr) 1fr minmax(0,1.7fr) 1.2fr .85fr .8fr 34px 44px";
/** The same tracks with the call-list checkbox in front. Only ever drawn for
 *  an operator who can place calls - see `useQueuePicks`. */
const PICK_COLS = `34px ${COLS}`;

/** What dialling this row means. Null when there is nothing to dial - the row
 *  still gets a (disabled) box, so the column has no holes in it. */
function targetOf(lead: ScanLead): CallTarget | null {
  if (!lead.phone) return null;
  return {
    phone: lead.phone,
    source: "scan-leads",
    companyName: lead.companyName ?? undefined,
    regCode: lead.regCode ?? undefined,
    leadDomain: lead.domain,
  };
}

type Sort = NonNullable<LeadListParams["sort"]>;

/** How badly the site needs the call. `lt` for hot is the design's "late" tone
 *  and it is the right one: hot means something on that site is broken now. */
const SEVERITY_TONE: Record<string, StatusTone> = {
  hot: "lt",
  warm: "wt",
  cool: "info",
};

const STATUS_TONE: Record<LeadStatus, StatusTone> = {
  new: "info",
  contacted: "wt",
  offer: "wt",
  won: "ok",
  ignored: "mut",
};

/**
 * A sortable column head.
 *
 * Was a `<button>` inside a `<th>`; it is a `<button>` inside the pill list's
 * head now, and it keeps `aria-sort` so the direction is announced rather than
 * only drawn as an arrow.
 */

export function LeadsTable({
  items,
  loading,
  total,
  page,
  pageSize,
  sort,
  order,
  onSort,
  onPage,
  onOpenScan,
  onOpenBrain,
  onStatus,
  onPushToCrm,
  onDismiss,
  canManage,
  picks,
  empty,
  t,
}: {
  items: ScanLead[];
  loading: boolean;
  total: number;
  page: number;
  pageSize: number;
  sort: Sort;
  order: "asc" | "desc";
  onSort: (c: Sort) => void;
  onPage: (p: number) => void;
  /** The row's own action: the scan report this lead was made from. */
  onOpenScan: (domain: string) => void;
  onOpenBrain: (domain: string) => void;
  onStatus: (domain: string, status: LeadStatus) => void;
  onPushToCrm: (domain: string) => void;
  onDismiss: (domain: string) => void;
  canManage: boolean;
  /** The call-list selection, or null for anyone who cannot place calls - in
   *  which case the column is not drawn at all. */
  picks: QueuePicks | null;
  /** What the card says when there are no rows. The caller picks, because an
   *  empty queue and a narrowed one are opposite facts that need different
   *  sentences and different buttons (see `ScanLeadsSection`). */
  empty: { title: string; body: string; action?: ReactNode };
  t: T;
}) {
  const [pushing, setPushing] = useState<string | null>(null);
  const menu = useContextMenu<ScanLead>();
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const cols = picks ? PICK_COLS : COLS;
  const pageTargets = items.flatMap((lead) => {
    const target = targetOf(lead);
    return target ? [{ key: lead.domain, target }] : [];
  });

  if (loading && items.length === 0) {
    return (
      <div className="crm-card flex justify-center py-16 text-muted-foreground">
        <Target className="size-6 animate-pulse" />
      </div>
    );
  }

  if (!loading && items.length === 0) {
    return (
      <div className="crm-card">
        <EmptyState icon={Target} title={empty.title} body={empty.body} action={empty.action} />
      </div>
    );
  }

  return (
    <div className="crm-card" style={listCard(cols)}>
      <div className="crm-list-head" style={{ gridTemplateColumns: cols, paddingTop: 22 }}>
        {picks ? <QueuePickAll picks={picks} entries={pageTargets} /> : null}
        <SortHead label={t("leads.col.severity")} column="severity" sort={sort} order={order} onSort={onSort} />
        <SortHead label={t("leads.col.company")} column="company" sort={sort} order={order} onSort={onSort} />
        <span>{t("leads.col.platform")}</span>
        <span>{t("leads.col.reasons")}</span>
        <span>{t("leads.col.contact")}</span>
        <SortHead label={t("leads.col.checked")} column="checked" sort={sort} order={order} onSort={onSort} />
        <SortHead label={t("leads.col.status")} column="status" sort={sort} order={order} onSort={onSort} />
        {/* The brain column has no label: the glyph is the whole message and
            a word here would cost more width than the track has. */}
        <span aria-hidden />
        <span />
      </div>

      <div className="crm-rows pt-0">
        {items.map((lead) => (
          <div
            key={lead.domain}
            className="crm-list-row relative"
            style={{ gridTemplateColumns: cols }}
            onContextMenu={(e) => menu.open(e, lead)}
          >
            {picks ? <QueuePick picks={picks} id={lead.domain} target={targetOf(lead)} /> : null}

            {/* Severity carries the score: the two are the same judgement said
                twice, and the number is what you sort by. */}
            <span className="flex items-center gap-2 pl-3">
              <StatusPill tone={SEVERITY_TONE[lead.severity] ?? "mut"}>
                {t(`leads.severity.${lead.severity}`)}
              </StatusPill>
              <span className="crm-tnum text-[13px] font-bold">{lead.score}</span>
            </span>

            <Company lead={lead} onOpenScan={() => onOpenScan(lead.domain)} t={t} />

            <span className="min-w-0">
              {lead.cmsName ? (
                <>
                  <span className="crm-trunc block text-[13px]">{lead.cmsName}</span>
                  <span className="crm-prow-s crm-tnum crm-trunc block">
                    {lead.cmsVersion ?? "?"}
                    {lead.cmsLatest ? ` / ${lead.cmsLatest}` : ""}
                  </span>
                </>
              ) : (
                <span className="crm-mut text-[12.5px]">-</span>
              )}
            </span>

            <Reasons lead={lead} t={t} />

            <span className="flex min-w-0 flex-col gap-0.5">
              {lead.email ? (
                <a
                  href={`mailto:${lead.email}`}
                  // Above the row-covering overlay, or the mail link is dead.
                  className="crm-trunc relative z-[1] inline-flex items-center gap-1 text-[12.5px] text-[var(--color-primary)] hover:underline"
                >
                  <Mail className="size-3 shrink-0" />
                  <span className="crm-trunc">{lead.email}</span>
                </a>
              ) : null}
              {lead.phone ? (
                <PhoneLink
                  phone={lead.phone}
                  source="scan-leads"
                  companyName={lead.companyName ?? undefined}
                  regCode={lead.regCode ?? undefined}
                  leadDomain={lead.domain}
                  stopPropagation
                  className="crm-trunc relative z-[1] text-[12.5px]"
                />
              ) : null}
              {!lead.email && !lead.phone ? <span className="crm-mut text-[12.5px]">-</span> : null}
            </span>

            <span className="crm-mut crm-tnum text-[12.5px]" title={fmtDateTime(lead.checkedAt)}>
              {formatWhen(lead.checkedAt)}
            </span>

            <span>
              <StatusPill tone={STATUS_TONE[lead.status]}>{t(`leads.status.${lead.status}`)}</StatusPill>
            </span>

            <BrainBadgeCell brain={lead.brain} t={t} />

            <LeadRowMenu
              lead={lead}
              canManage={canManage}
              busy={pushing === lead.domain}
              t={t}
              onOpenScan={() => onOpenScan(lead.domain)}
              onOpenBrain={() => onOpenBrain(lead.domain)}
              onStatus={(s) => onStatus(lead.domain, s)}
              onPushToCrm={() => {
                setPushing(lead.domain);
                onPushToCrm(lead.domain);
                setTimeout(() => setPushing(null), 1200);
              }}
              onDismiss={() => onDismiss(lead.domain)}
            />
          </div>
        ))}
      </div>

      {pages > 1 ? (
        <div className="crm-tfoot">
          {t("scanner.table.page", { page, pages })}
          <span className="crm-push flex gap-2.5">
            <Btn small disabled={page <= 1} onClick={() => onPage(page - 1)}>
              <ChevronLeft className="size-4" />
              {t("common.prev")}
            </Btn>
            <Btn small disabled={page >= pages} onClick={() => onPage(page + 1)}>
              {t("common.next")}
              <ChevronRight className="size-4" />
            </Btn>
          </span>
        </div>
      ) : null}

      {/* The same entries as the row's three-dot button, at the pointer. */}
      <ContextMenu menu={menu} label={t("leads.rowMenu")}>
        {(lead, close) => (
          <LeadRowItems
            lead={lead}
            canManage={canManage}
            t={t}
            close={close}
            head
            onOpenScan={() => onOpenScan(lead.domain)}
            onOpenBrain={() => onOpenBrain(lead.domain)}
            onStatus={(s) => onStatus(lead.domain, s)}
            onPushToCrm={() => {
              setPushing(lead.domain);
              onPushToCrm(lead.domain);
              setTimeout(() => setPushing(null), 1200);
            }}
            onDismiss={() => onDismiss(lead.domain)}
          />
        )}
      </ContextMenu>
    </div>
  );
}
